import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Hammer, AlertTriangle, CheckCircle2 } from 'lucide-react';

const BUILD_ROLES = ['Admin', 'ProductionSupervisor'];
const RP_ROLES = ['Admin', 'InventoryManager', 'ProductionSupervisor'];

const BuildRow = ({ a, canBuild, canSetRp, onBuild, onRp }) => {
  const [qty, setQty] = useState(Math.max(a.suggested_qty, 1));
  const [rp, setRp] = useState(a.reorder_point);
  return (
    <div className={`grid grid-cols-[1fr_auto] md:grid-cols-[1fr_7rem_7rem_7rem_auto] gap-3 items-center px-3 py-2 ${a.low ? 'bg-rose-50/40' : ''}`} data-testid={`build-row-${a.item_sku}`}>
      <div className="min-w-0">
        <div className="text-sm font-medium text-slate-800 truncate flex items-center gap-1">
          {a.low ? <AlertTriangle className="w-3.5 h-3.5 text-rose-600" /> : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />}{a.name}
        </div>
        <div className="text-[11px] mono text-slate-500">{a.item_sku}{a.bom_revision ? ` · builds from rev ${a.bom_revision}` : ' · no Released BOM'}</div>
      </div>
      <div className="text-xs"><span className={`mono font-semibold ${a.low ? 'text-rose-600' : 'text-slate-700'}`} data-testid={`build-on-hand-${a.item_sku}`}>{a.on_hand}</span> <span className="text-slate-400">on hand</span></div>
      <div className="text-xs flex items-center gap-1">
        <span className="text-slate-400">reorder at</span>
        {canSetRp ? (
          <Input type="number" min={0} value={rp} onChange={e => setRp(e.target.value)} onBlur={() => Number(rp) !== a.reorder_point && onRp(a, Number(rp) || 0)} className="h-7 w-14 text-xs mono px-1" data-testid={`build-rp-input-${a.item_sku}`} />
        ) : <span className="mono">{a.reorder_point}</span>}
      </div>
      <div className="text-xs text-slate-500" data-testid={`build-open-${a.item_sku}`}>{a.open_builds} open build{a.open_builds === 1 ? '' : 's'}</div>
      {canBuild && (
        <div className="flex items-center gap-1 col-start-2 md:col-start-auto">
          <Input type="number" min={1} max={50} value={qty} onChange={e => setQty(e.target.value)} className="h-8 w-14 text-xs mono px-1" data-testid={`build-qty-${a.item_sku}`} />
          <Button size="sm" disabled={!a.bom_id} onClick={() => onBuild(a, Number(qty) || 1)} className="h-8 bg-orange-600 hover:bg-orange-700" data-testid={`build-create-wo-${a.item_sku}`}>
            <Hammer className="w-3.5 h-3.5 mr-1" />Create build WO
          </Button>
        </div>
      )}
    </div>
  );
};

export const AssembliesToBuildPanel = () => {
  const { api, user } = useAuth();
  const [rows, setRows] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const load = useCallback(() => {
    api().get('/structure/assemblies-to-build', { params: { include_ok: showAll } }).then(r => setRows(r.data)).catch(() => setRows([]));
  }, [api, showAll]);
  useEffect(() => { load(); }, [load]);

  const onBuild = async (a, quantity) => {
    try {
      const { data } = await api().post(`/structure/assemblies/${a.product_type_id}/build`, { quantity });
      toast.success(`Created ${data.work_order_ids.length} build WO${data.work_order_ids.length === 1 ? '' : 's'} for ${a.name} (rev ${data.bom_revision})`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create build WO'); }
  };
  const onRp = async (a, reorder_point) => {
    try { await api().put(`/structure/assemblies/${a.product_type_id}/reorder-point`, { reorder_point }); toast.success(`Reorder point for ${a.name} set to ${reorder_point}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not save reorder point'); }
  };

  const low = (rows || []).filter(r => r.low).length;
  return (
    <Card className={`industrial-card overflow-hidden ${low ? 'border-rose-200' : ''}`} data-testid="assemblies-to-build-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div>
          <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Hammer className="w-4 h-4 text-orange-600" />Assemblies to build
            <span className={`text-[11px] mono px-1.5 rounded ${low ? 'bg-rose-100 text-rose-700' : 'bg-emerald-50 text-emerald-700'}`} data-testid="assemblies-to-build-count">{low} at/below reorder point</span>
          </h2>
          <p className="text-[11px] text-slate-500">Assemblies are also included in the daily low-stock alert.</p>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
          <Switch checked={showAll} onCheckedChange={setShowAll} data-testid="assemblies-to-build-show-all" /> Show all assemblies
        </label>
      </div>
      <div className="divide-y divide-slate-100">
        {rows === null ? <p className="p-4 text-sm text-slate-500">Loading…</p>
          : rows.length === 0 ? <p className="p-4 text-sm text-slate-500" data-testid="assemblies-to-build-empty">{showAll ? 'No assemblies yet.' : 'All assemblies are above their reorder points.'}</p>
          : rows.map(a => <BuildRow key={`${a.product_type_id}-${a.reorder_point}-${a.on_hand}`} a={a} canBuild={BUILD_ROLES.includes(user?.role)} canSetRp={RP_ROLES.includes(user?.role)} onBuild={onBuild} onRp={onRp} />)}
      </div>
    </Card>
  );
};
